/**
 * Analyzer: Loss Size Ratio (Wave A, v7.4.4)
 *
 * Tom Hougaard's cardinal metric from "Best Loser Wins": average
 * loser must be SMALLER than average winner. When |avg loss| / avg win
 * exceeds 1.0, you're losing more per losing trade than you're making
 * per winning trade — your win rate has to be insanely high to be
 * net-profitable, and most traders aren't.
 *
 * Bands (configurable per-user later):
 *   - ratio ≤ 0.70  → SUCCESS  "Healthy" (positive reinforcement)
 *   - 0.70 < ≤ 1.0  → WARNING  "Concerning" (approaching break-even territory)
 *   - ratio  > 1.0  → CRITICAL "Losers exceed winners — single biggest leverage point"
 *
 * Window: rolling 30 trading days, configurable via ctx.windowDays.
 * Excludes break-even trades (pnl = 0). Excludes the rare degenerate
 * case where avg win is ≤ 0 (no winners → no meaningful ratio).
 *
 * Reads:
 *   trade.date, trade.pnl
 *
 * Fires at most ONE observation per analyze() call (the current state),
 * not per-trade. Surfaces in the Live Coach Feed's "30D TRENDS" section.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';
import { getTradingDay, addDaysToDateStr } from '../../../timeService.js';

export const key = 'lossSizeRatio';
export const tier = 'free';

// Sample-size guards — need this many winners and losers BOTH to compute a
// meaningful ratio. Below this, the ratio bounces around too much to act on.
const MIN_WINS = 5;
const MIN_LOSSES = 5;

// Ratio bands
const HEALTHY_RATIO    = 0.70;
const CONCERNING_RATIO = 1.00;

const inWindow = (trade, fromDateStr, toDateStr) => {
  if (!trade?.date || typeof trade.date !== 'string') return false;
  return trade.date >= fromDateStr && trade.date <= toDateStr;
};

export const analyze = (ctx) => {
  const { allTrades, currentTradingDay, windowDays = 30 } = ctx || {};
  if (!Array.isArray(allTrades) || allTrades.length === 0) return null;

  const anchor = currentTradingDay || getTradingDay(Date.now()) || null;
  if (!anchor) return null;
  const fromDateStr = addDaysToDateStr(anchor, -windowDays);

  // Window-filter + classify in one pass. We read trade.pnl directly
  // (the persisted net P&L the rest of the app already trusts).
  const wins = [];
  const losses = [];
  for (const t of allTrades) {
    if (!inWindow(t, fromDateStr, anchor)) continue;
    const pnl = parseFloat(t.pnl);
    if (!Number.isFinite(pnl) || pnl === 0) continue;
    if (pnl > 0) wins.push(pnl);
    else losses.push(pnl);
  }

  if (wins.length < MIN_WINS || losses.length < MIN_LOSSES) return null;

  const avgWin = wins.reduce((s, n) => s + n, 0) / wins.length;
  const avgLoss = Math.abs(losses.reduce((s, n) => s + n, 0) / losses.length);
  if (avgWin <= 0) return null;
  const ratio = avgLoss / avgWin;

  // Pick severity + copy by band
  let severity, title, action, priority;
  if (ratio <= HEALTHY_RATIO) {
    severity = SEVERITY.SUCCESS;
    title = `Loss-size ratio healthy at ${ratio.toFixed(2)}× (target ≤ 0.70)`;
    action = 'Discipline is working — keep cutting losers fast and letting winners run. Don’t tighten stops looking for a fix to a non-problem.';
    priority = 55;
  } else if (ratio <= CONCERNING_RATIO) {
    severity = SEVERITY.WARNING;
    title = `Loss-size ratio ${ratio.toFixed(2)}× — losers approaching winner size`;
    action = `Average loser ($${avgLoss.toFixed(0)}) is ${ratio.toFixed(2)}× your average winner ($${avgWin.toFixed(0)}). Hougaard’s rule: losers should be smaller than winners. Tighten the loss side, or hold winners longer when the setup is working.`;
    priority = 70;
  } else {
    severity = SEVERITY.CRITICAL;
    title = `Loss-size ratio ${ratio.toFixed(2)}× — losers larger than winners`;
    action = `Average loser ($${avgLoss.toFixed(0)}) is LARGER than average winner ($${avgWin.toFixed(0)}) over ${windowDays}D. This single metric has more leverage on your bottom line than any new setup — even a 60% win rate net-loses money when this ratio is above 1.0. Focus here before anything else.`;
    priority = 88;
  }

  return makeObservation({
    id: `${key}:${anchor}`,
    analyzer: key,
    scope: 'progress',
    scopeId: anchor,
    severity,
    category: CATEGORY.PROCESS,
    title,
    detail: `${wins.length} winners avg $${avgWin.toFixed(0)} · ${losses.length} losers avg $${avgLoss.toFixed(0)} over ${windowDays}D · ratio ${ratio.toFixed(2)}× (target ≤ 0.70).`,
    action,
    evidence: {
      ratio,
      avgWin,
      avgLoss,
      winCount: wins.length,
      lossCount: losses.length,
      windowDays,
      bands: { healthy: HEALTHY_RATIO, concerning: CONCERNING_RATIO },
    },
    confidence: 0.85,
    priority,
  });
};
